import { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Gradebook from './components/Gradebook';
import { ScreenReaderAnnouncements } from './components/ScreenReaderAnnouncements';
import MLLoading from '../../MLLoading/MLLoading';
import {
  transformReduxToV2Format,
  createCalculationsFromV2Data,
} from './adapters/reduxDataAdapter';
import { useGradebookSearchAnnouncement } from './hooks/useGradebookSearchAnnouncement';
import styles from './InstructorGradebookV2.module.css';

function InstructorGradebookV2({
  gradebookGridData,
  collapsedCategoryIds,
  showPercent,
  loading,
  isGradebookFetching,
  isGradebookReFetching,
  courseId,
  dispatch,
  pageCount,
  getNextGradebookPage,
  searchPhrase,
  searchType,
  showGradeOverrideDialog,
  hasInstructorLinkedCourse,
  rdxGradebookSettings,
  sortColumn,
  sortDirection,
  hasStudentReport,
  getStudentReportLink,
  getLaunchUrl,
  averages,
  updateAssignmentAverages,
  onTabChange,
  disableManualAssignmentsMenu,
  page,
  limit,
  count,
  getGradebook,
  getGradebookInstructor,
  exceptionGroups,
  filterTypes,
}) {
  const [announcement, setAnnouncement] = useState('');

  const announceToScreenReader = useCallback((message) => {
    setAnnouncement(message);
  }, []);

  // Transform Redux data to V2 format
  const v2Data = useMemo(() => {
    if (!gradebookGridData || !gradebookGridData.rows) {
      return null;
    }
    return transformReduxToV2Format(gradebookGridData, {
      showPercent,
      collapsedCategoryIds,
      pageCount,
    });
  }, [gradebookGridData, showPercent, collapsedCategoryIds, pageCount]);

  // Pre-calculate all student totals and averages
  const calculations = useMemo(() => {
    if (!v2Data) {
      return null;
    }
    return createCalculationsFromV2Data(v2Data);
  }, [v2Data]);

  // Show loading state if data is being fetched
  const isLoading = loading || (isGradebookFetching && !isGradebookReFetching);

  useGradebookSearchAnnouncement({
    searchPhrase,
    searchType,
    gradebookGridData,
    isGradebookFetching,
    isGradebookReFetching,
    isLoading,
    isReady: Boolean(v2Data),
  });

  const liveRegion = <ScreenReaderAnnouncements announcement={announcement} />;

  if (isLoading || !v2Data) {
    return (
      <>
        {liveRegion}
        <div className={styles.spinnerContainer}>
          <MLLoading />
        </div>
      </>
    );
  }

  return (
    <div className={styles.container} id="gradebook-main">
      {liveRegion}
      <Gradebook
        data={v2Data}
        calculations={calculations}
        onAnnouncement={announceToScreenReader}
        showPercent={showPercent}
        collapsedCategoryIds={collapsedCategoryIds}
        courseId={courseId}
        dispatch={dispatch}
        pageCount={pageCount}
        getNextGradebookPage={getNextGradebookPage}
        showGradeOverrideDialog={showGradeOverrideDialog}
        hasInstructorLinkedCourse={hasInstructorLinkedCourse}
        rdxGradebookSettings={rdxGradebookSettings}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        hasStudentReport={hasStudentReport}
        getStudentReportLink={getStudentReportLink}
        getLaunchUrl={getLaunchUrl}
        averages={averages}
        updateAssignmentAverages={updateAssignmentAverages}
        searchPhrase={searchPhrase}
        searchType={searchType}
        onTabChange={onTabChange}
        isGradebookReFetching={isGradebookReFetching}
        disableManualAssignmentsMenu={disableManualAssignmentsMenu}
        page={page}
        pageSize={limit}
        count={count}
        getGradebook={getGradebook}
        getGradebookInstructor={getGradebookInstructor}
        exceptionGroups={exceptionGroups}
        filterTypes={filterTypes}
      />
    </div>
  );
}

InstructorGradebookV2.propTypes = {
  gradebookGridData: PropTypes.shape({
    rows: PropTypes.array.isRequired,
    rowCount: PropTypes.number,
    columnCount: PropTypes.number,
    leftGridColumnCount: PropTypes.number,
    hasStudents: PropTypes.bool,
    hasAssignments: PropTypes.bool,
  }),
  collapsedCategoryIds: PropTypes.array,
  showPercent: PropTypes.bool,
  loading: PropTypes.bool,
  isGradebookFetching: PropTypes.bool,
  isGradebookReFetching: PropTypes.bool,
  courseId: PropTypes.string,
  dispatch: PropTypes.func,
  pageCount: PropTypes.number,
  getNextGradebookPage: PropTypes.func,
  searchPhrase: PropTypes.string,
  searchType: PropTypes.string,
  showGradeOverrideDialog: PropTypes.func,
  hasInstructorLinkedCourse: PropTypes.bool,
  rdxGradebookSettings: PropTypes.array,
  sortColumn: PropTypes.string,
  sortDirection: PropTypes.string,
  hasStudentReport: PropTypes.bool,
  getStudentReportLink: PropTypes.func,
  getLaunchUrl: PropTypes.func,
  averages: PropTypes.array,
  updateAssignmentAverages: PropTypes.func,
  onTabChange: PropTypes.func,
  disableManualAssignmentsMenu: PropTypes.bool,
  page: PropTypes.number,
  limit: PropTypes.number,
  count: PropTypes.number,
  getGradebook: PropTypes.func,
  getGradebookInstructor: PropTypes.func,
  exceptionGroups: PropTypes.array,
  filterTypes: PropTypes.array,
};

InstructorGradebookV2.defaultProps = {
  gradebookGridData: null,
  collapsedCategoryIds: [],
  showPercent: false,
  loading: false,
  isGradebookFetching: false,
  isGradebookReFetching: false,
  courseId: '',
  dispatch: () => {},
  pageCount: 1,
  getNextGradebookPage: () => {},
  searchPhrase: '',
  searchType: 'STUDENT',
  showGradeOverrideDialog: () => {},
  hasInstructorLinkedCourse: false,
  rdxGradebookSettings: [],
  sortColumn: null,
  sortDirection: null,
  hasStudentReport: false,
  getStudentReportLink: null,
  getLaunchUrl: null,
  averages: [],
  updateAssignmentAverages: () => {},
  onTabChange: () => {},
  disableManualAssignmentsMenu: false,
  page: 1,
  limit: 25,
  count: 0,
  getGradebook: () => {},
  getGradebookInstructor: null,
  exceptionGroups: [],
  filterTypes: [],
};

export default InstructorGradebookV2;
